(ns frontend.view.settings2.deal-flow-pipelines.view
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.view.entity :as f-entity]
   [frontend.view.modals.curtains.pipeline-settings-curtain.view :as pipeline-settings-curtain]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.pipeline-edit.view :as pipeline-edit]
   [frontend.view.settings2.creation-flow-toggle.view :as creation-flow-toggle]
   [frontend.view.settings2.deal-flow-pipelines.db :as db]
   [reagent.core :as r]
   [ui.components.breadcrumbs.view :as breadcrumbs]
   [ui.components.button.view :as button]))

(defn breadcrumbs
  []
  [breadcrumbs/container
   [breadcrumbs/item
    {:on-click (fn [_e]
                 (routing/set-view! [:account-settings :tab-key "config"]))}
    (loc/tr :settings/account-settings)]
   [breadcrumbs/item
    {:active? true}
    (loc/tr :settings/deal-pipelines)]])

(defn pipeline-row
  [all-deal-pipelines all-teams pipeline]
  (let [disabled? (:common/disabled? pipeline)
        inactive? (:pipeline/inactive? pipeline)
        pipeline-name (:pipeline/name pipeline)
        pipeline-fund (get @session/entities (:pipeline/fund pipeline))
        pipeline-asset-types (:pipeline/target-asset-types pipeline)
        pipeline-teams (->> pipeline
                            :pipeline/teams
                            (map (fn [team-id]
                                   (get @session/entities team-id)))
                            (remove :common/disabled?))
        pipeline-id (:db/id pipeline)
        pipeline-stages (->> @session/all-stages
                             (remove :common/disabled?)
                             (cu/filter-by :stage/pipeline pipeline-id)
                             (sort-by :common/ord))
        other-pipelines (->> all-deal-pipelines
                             (remove (fn [{:keys [db/id]}]
                                       (= pipeline-id id))))]
    [:tr
     {:class-name "*:whitespace-nowrap *:px-2 *:py-3 *:text-sm *:text-base-light-text "
      :key        (str "pipeline-row-" pipeline-name)}
     [:td
      (cond disabled?
            (loc/tr :disabled)
            inactive?
            (loc/tr :archived)
            :else
            (loc/tr :active))]
     [:td pipeline-name]
     [:td
      [:ul
       (doall
        (for [target-type pipeline-asset-types]
          ^{:key (str "pipeline-asset-type-" pipeline-name "-" target-type)}
          [:li
           {:class "flex items-center"}
           (loc/tr target-type)]))]]
     [:td
      [:ul
       (->> pipeline-teams
            (map (fn [team]
                   [:li
                    {:key (str "pipeline-team-" pipeline-name "-" (:db/id team))}
                    (entity/display-string team)]))
            (doall))]]

     [:td
      [:div
       {:class "flex"}
       [:ul
        {:class "flex flex-wrap gap-1"}
        (->> pipeline-stages
             (map (fn [stage]
                    [:li
                     {:key   (str "pipeline-stage-" pipeline-name "-" (:db/id stage))
                      :class "mb-0 py-1 px-2 text-sm bg-complement-regular rounded-full"}
                     (entity/display-string stage)]))
             (doall))]
       (when-not disabled?
         [button/square-secondary-open
          {:dom-id (str "pipeline-stage-edit-button-" pipeline-name)
           :tooltip  (loc/tr :tooltip/configure-pipeline-stages)
           :class    "pt-2"
           :on-click (fn [_]
                       (modals/open-modal!
                        pipeline-settings-curtain/modal-key
                        {:pipeline-id          pipeline-id
                         :stage-entity-type    :entity.type/stage
                         :stage-name-attribute :stage/name
                         :pipeline-attribute   :stage/pipeline}))}
          [:i.fas.fa-pen]])]]
     [:td
      (str (entity/display-string pipeline-fund))]
     [:td
      (when-not disabled?
        [button/square-secondary-open
         {:tooltip  (loc/tr :show-permissions)
          :on-click (fn [_]
                      (routing/set-view! [:settings.permissions/pipeline :id
                                          pipeline-id]))}
         [:i.fas.fa-user-lock]])]
     [:td
      (when-not disabled?
        [button/square-secondary-open
         {:dom-id (str "pipeline-edit-more-options-" pipeline-id)
          :tooltip  (loc/tr :edit)
          :on-click (fn [_]
                      (modals/open-modal! pipeline-edit/modal-key
                                          {:pipeline-id        pipeline-id
                                           :existing-pipelines other-pipelines
                                           :existing-teams     all-teams}))}
         [:i.fas.fa-ellipsis-v]])]]))

(defn most-common-target-types
  "The most common combination of :pipeline/target-asset-types of given pipelines.
   If multiple combinations are as common, the one is picked where
   the pipeline is the most recently created"
  [pipelines]
  (let [freqs (->> pipelines
                   (mapv :pipeline/target-asset-types)
                   (mapv set)
                   frequencies
                   (sort-by second)
                   reverse)
        most-common-count (->> freqs first second)
        most-common-ones (->> freqs
                              (take-while (fn [[_ c]]
                                            (= c most-common-count)))
                              (mapv first)
                              set)]
    (->> pipelines
         (filterv (fn [{:keys [pipeline/target-asset-types]}]
                    (contains? most-common-ones (set target-asset-types))))
         (sort-by :common/created-on)
         last
         :pipeline/target-asset-types)))

(defn component
  []
  (r/create-class
   {:reagent-render
    (fn []
      (let [all-teams @session/teams
            all-deal-pipelines @session/all-oppo-pipelines]
        [:<>
         [zap-toolbar/view db/state breadcrumbs nil nil]
         [:div
          {:class "flex flex-col flex-1 bg-base-light h-[calc(100vh_-_52px-_50px)] overflow-auto"}
          [:div
           {:class "flex flex-col flex-1 px-4 py-4"}
           (if (empty? all-deal-pipelines)
             [:div
              {:class "w-screen flex flex-col gap-6 justify-center items-center"}
              [:img
               {:src        "/img/no-data-created.svg"
                :class-name "max-w-32 mx-auto"}]
              [button/sm-primary-solid
               {:on-click (fn [_e]
                            (f-entity/open-creator! :entity.type/pipeline
                                                    {:start-value {:pipeline/entity-type
                                                                   :entity.type/oppo}
                                                     :on-ok!      (fn [{:keys [close-cb!]}]
                                                                    (close-cb!))}))}
               [:span [:i {:class "fal fa-plus"}]] (loc/tr :create)]]
             [:div
              {:class "max-w-full md:p-3 inline-flex flex-col justify-between gap-4"}

              (when (session/froppo-creation-flow-feature?)
                [:div
                 {:class "flex flex-row gap-4"}
                 [creation-flow-toggle/view
                  {:preference-key    :deal-creation-flow
                   :title-loc-key     :new-deal-creation-flow
                   :for-me-loc-key    :deal-creation-flow-toggle-for-me
                   :for-all-loc-key   :deal-creation-flow-toggle-for-all
                   :info-loc-key      :creation-flow-toggle-info
                   :info-icon-tooltip (loc/tr :deal-creation-flow-toggle-info-icon
                                              [(loc/tr :creator-config-item-label)])}]

                 (when (and (session/entry-criteria-feature?)
                            (session/deal-creation-flow?))
                   [creation-flow-toggle/view
                    {:info-icon-tooltip (loc/tr :stage-entry-criteria-info)
                     :preference-key :stage-entry-criteria-deal
                     :title-loc-key :stage-entry-criteria-for-deals
                     :for-me-loc-key :toggle-for-me
                     :for-all-loc-key :toggle-for-all}])])

              [:div {:class "flex justify-between items-center"}
               [:h2
                {:class "font-semibold text-xl"}
                (loc/tr :settings.pipelines/pipelines-heading)]

               [button/sm-primary-solid
                {:dom-id   "create-deal-pipelines-button"
                 :on-click (fn [_e]
                             (f-entity/open-creator! :entity.type/pipeline
                                                     {:start-value {:pipeline/entity-type
                                                                    :entity.type/oppo}
                                                      :on-ok!      (fn [{:keys [close-cb!]}]
                                                                     (close-cb!))}))}
                [:span [:i {:class "fal fa-plus"}]] (loc/tr :settings/create-deal-pipelines)]]
              [:div
               {:class "max-w-full overflow-auto border border-solid border-base-light-border-paler rounded-lg overflow-x-auto"}
               [:table
                {:class "w-full lg:w-auto divide-y divide-solid divide-base-light-border-paler"}
                [:thead
                 [:tr
                  {:class "*:whitespace-nowrap *:px-2 *:py-3 *:text-sm *:text-base-light-text even:bg-base-regular"}
                  [:th (loc/tr :settings.pipelines/table-heading-status)]
                  [:th (loc/tr :settings.pipelines/table-heading-name)]
                  [:th (loc/tr :target-asset-types)]
                  [:th (loc/tr :settings.pipelines/table-heading-teams)]
                  [:th (loc/tr :settings.pipelines/table-heading-stages)]
                  [:th (loc/tr :settings.pipelines/table-heading-fund)]
                  [:th (loc/tr :settings.pipelines/table-heading-permissions)]
                  [:th (loc/tr :settings.pipelines/table-heading-edit)]]]
                [:tbody
                 {:class "divide-y divide-solid divide-base-light-border-paler"}
                 (->> all-deal-pipelines
                      (sort-by :pipeline/name)
                      (map (partial pipeline-row all-deal-pipelines all-teams))
                      (doall))]]]])]]]))}))

(defn render-view
  []
  [default-page-layout/view
   [component]])

(defmethod routing/render-view :settings/deal-flow-pipelines
  [_]
  [render-view])
